import { useEffect } from "react"
import PubSub from "pubsub-js"
import rt, { disconnectSocket } from "aldoo-ra/Realtime"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import { showSuccessToast, TOAST_POSITIONS } from "aldoo-ra/Toast"

// Bridges the /rt/account Socket.io namespace to PubSub topics. Components
// (Dashboard, ShareDrawer, ReceiveLinks, Sent) subscribe to those topics
// and patch their local state without a refresh.
//
// PubSub topic naming:
//   transfer.share.created
//   transfer.share.status
//   transfer.share.download
//   transfer.share.scan
//   transfer.share.access
//   transfer.share.deleted
//   transfer.share.received
//   transfer.receivelink.created
//   transfer.receivelink.updated
//   transfer.receivelink.quota_blocked

const SHARE_EVENTS = [
    "share.created", "share.status", "share.download", "share.scan",
    "share.access", "share.deleted", "share.received",
]
const LINK_EVENTS = [
    "receivelink.created", "receivelink.updated", "receivelink.quota_blocked",
]
const THEME_EVENTS = ["theme.changed"]
// Plan-level events. account.plan_changed fires after a SUBSCRIPTION_CANCELLED
// cascade so the planChip / Usage caps update without a refetch.
const ACCOUNT_EVENTS = ["account.plan_changed"]

export default function useAccountRealtime() {
    const [auth] = PlatformAuth.useAuth()
    useEffect(() => {
        if (!auth?._id) return
        let socket
        try {
            socket = rt("account")
        } catch {
            return
        }
        const offs = []
        const wire = (event) => {
            const handler = (payload) => {
                PubSub.publish(`transfer.${event}`, payload)
                // Inbound transfer toast — single line, owner sees who sent
                // and how many files.
                if (event === "share.received") {
                    const sender = payload?.sender_name || payload?.sender_email || "Someone"
                    const filesLabel = payload?.file_count > 1
                        ? `${payload.file_count} files`
                        : (payload?.title || "a file")
                    showSuccessToast(
                        `${sender} sent you ${filesLabel}`,
                        "",
                        {
                            position: TOAST_POSITIONS.TOP_CENTER,
                            pauseOnHover: false,
                            enableDismissalTracking: false,
                            duration: 4000,
                        },
                    )
                }
            }
            socket.on(event, handler)
            offs.push(() => socket.off(event, handler))
        }
        SHARE_EVENTS.forEach(wire)
        LINK_EVENTS.forEach(wire)
        THEME_EVENTS.forEach(wire)
        ACCOUNT_EVENTS.forEach(wire)
        return () => {
            for (const off of offs) try { off() } catch {}
            // Keep the socket alive across remounts in the same Account
            // tree — only fully disconnect when auth flips.
        }
    }, [auth?._id])

    useEffect(() => () => { try { disconnectSocket("account") } catch {} }, [])
}
